/**
 * P-05/E, H — the milestones list rules (the project's "אבני דרך" tab and
 * /milestones), pure and unit-tested (`src/utils/__tests__/milestoneListModel.test.ts`).
 */
import { isOverdue } from '../lists/listsModel';

export interface ListMilestone {
  id: string;
  name: string;
  status: string;
  is_core?: boolean | null;
  parent_milestone_id?: string | null;
  archived_at?: string | null;
  start_date?: string | null;
  due_date?: string | null;
  responsible_user_id?: string | null;
  assignees?: { user_id: string; name?: string }[] | null;
}

export interface MilestoneFilters {
  search: string;
  status: string;
  responsible: string;
  assignee: string;
  /** Only the milestones past their due date (not completed). */
  overdue: boolean;
  /** Only the completed milestones. */
  completed: boolean;
}

export const emptyMilestoneFilters = (): MilestoneFilters => ({ search: '', status: '', responsible: '', assignee: '', overdue: false, completed: false });

export function isMilestoneOverdue(m: Pick<ListMilestone, 'due_date' | 'status'>, today: Date = new Date()): boolean {
  return isOverdue(m.due_date, m.status === 'completed', today);
}

export function matchesMilestoneFilters(m: ListMilestone, f: MilestoneFilters, today?: Date): boolean {
  if (f.search && !m.name.toLowerCase().includes(f.search.trim().toLowerCase())) return false;
  if (f.status && m.status !== f.status) return false;
  if (f.responsible && m.responsible_user_id !== f.responsible) return false;
  if (f.assignee && !(m.assignees ?? []).some((a) => a.user_id === f.assignee)) return false;
  if (f.overdue && !isMilestoneOverdue(m, today)) return false;
  if (f.completed && m.status !== 'completed') return false;
  return true;
}

export const hasMilestoneFilters = (f: MilestoneFilters): boolean =>
  !!(f.search || f.status || f.responsible || f.assignee || f.overdue || f.completed);

export interface MilestoneRowEntry<M extends ListMilestone> {
  milestone: M;
  /** 0 = a top-level milestone, 1 = a sub-milestone (indented under its parent). */
  depth: 0 | 1;
}

/**
 * E (D-39) — the rows in order: the milestones as the server sends them (the
 * 4 core ones first, in their fixed order, then the rest), each top-level
 * milestone followed by its sub-milestones (indented). A sub-milestone whose
 * parent is not in the list (filtered out) still shows, indented, after it
 * would have. Archived rows go to the folded "בארכיון" part.
 */
export function orderMilestoneRows<M extends ListMilestone>(milestones: M[], keep: (m: M) => boolean = () => true): {
  live: MilestoneRowEntry<M>[];
  archived: MilestoneRowEntry<M>[];
} {
  const byParent = new Map<string, M[]>();
  for (const m of milestones) {
    if (!m.parent_milestone_id) continue;
    const list = byParent.get(m.parent_milestone_id) ?? [];
    list.push(m);
    byParent.set(m.parent_milestone_id, list);
  }
  const ids = new Set(milestones.map((m) => m.id));
  const ordered: MilestoneRowEntry<M>[] = [];
  for (const m of milestones) {
    if (m.parent_milestone_id && ids.has(m.parent_milestone_id)) continue; // placed under its parent
    if (keep(m)) ordered.push({ milestone: m, depth: m.parent_milestone_id ? 1 : 0 });
    for (const sub of byParent.get(m.id) ?? []) {
      if (keep(sub)) ordered.push({ milestone: sub, depth: 1 });
    }
  }
  return {
    live: ordered.filter((r) => !r.milestone.archived_at),
    archived: ordered.filter((r) => !!r.milestone.archived_at),
  };
}

export interface TaskGroup<M> {
  m: M;
  depth: 0 | 1;
  /** QA-R2/1 (P-02a/C2) — an archived milestone's group is marked "(בארכיון)". */
  archived: boolean;
}

/**
 * The project's "משימות" tab: one group per milestone, in the tab's order. An
 * archived milestone shows only while a task still sits in it (and is marked
 * archived); with a filter on, only the groups with a matching task show.
 */
export function projectTaskGroups<M extends { id: string; archived_at?: string | null }>(
  ordered: ReadonlyArray<{ m: M; depth: 0 | 1 }>,
  taskCount: (milestoneId: string) => number,
  hasFilters: boolean
): TaskGroup<M>[] {
  return ordered
    .filter(({ m }) => !m.archived_at || taskCount(m.id) > 0)
    .filter(({ m }) => !hasFilters || taskCount(m.id) > 0)
    .map(({ m, depth }) => ({ m, depth, archived: !!m.archived_at }));
}

/**
 * QA-R2/5 (P-02a/C2, D-22, D-25) — the delete / archive item of a milestone's
 * ⋯, the same rule in the project's tab and in /milestones: a core milestone
 * none; an archived one "שחזור"; one with work or sub-milestones "העברה
 * לארכיון"; an empty one "מחיקה". Nothing without the permission.
 */
export type MilestoneRemoveAction = 'delete' | 'archive' | 'restore' | null;

export function milestoneRemoveAction(
  m: { is_core?: boolean | null; archived_at?: string | null; has_work?: boolean | null; has_sub_milestones?: boolean | null },
  canDelete: boolean
): MilestoneRemoveAction {
  if (!canDelete || m.is_core) return null;
  if (m.archived_at) return 'restore';
  return m.has_work || m.has_sub_milestones ? 'archive' : 'delete';
}
